/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.
*/

import {
	ChangeDetectionStrategy,
	Component,
	ViewEncapsulation,
	computed,
	inject,
	input
} from "@angular/core";
import type { Enums } from "@app/@classes/supabase";
import { UsersRepositoryService } from "@app/@services/repository/users-repository.service";
import { Subject } from "rxjs";
import { AvatarComponent } from "../avatar/avatar.component";

const RANDOM_AVATARS_COUNT = 60;

@Component({
	selector: "cb-user-avatar",
	standalone: true,
	imports: [AvatarComponent],
	templateUrl: "./user-avatar.component.html",
	styleUrl: "./user-avatar.component.scss",
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserAvatarComponent {
	private readonly usersRepositoryService: UsersRepositoryService = inject(
		UsersRepositoryService,
	);

	private destroy$ = new Subject<void>();

	public readonly type = input<Enums<"avatar_type">>();

	public readonly label = input<string | null>("");

	public readonly url = input<string | null>("");

	public readonly size = input<"xxs" | "xs" | "sm" | "md" | "lg" | "xl" | "2xl">("md");

	public readonly userId = input<string | null>("");

	public readonly subtitle = input<string | null>("");

	public readonly customInitials = input<string | null>("");

	public readonly onlyAvatar = input<boolean>(false);

	public readonly avatarsList = input<
		{
			id: string;
			name: string;
			profile_img: string;
			avatar_type: string;
			custom_initials: string;
		}[]
	>([]);

	public readonly randomIndex = 1;

	public readonly scaleParams = computed(() => {
		switch (this.size()) {
			case "xs":
				return "?quality=30&width=40";
			case "sm":
				return "?quality=40&width=60";
			case "md":
				return "?quality=40&width=80";
			case "lg":
				return "?quality=50&width=100";
			case "xl":
				return "?quality=60&width=150";
			default:
				return "?quality=60&width=200";
		}
	});

	public readonly avatarsMap = computed(() => {
		if (this.avatarsList()?.length > 0) {
			return this.avatarsList().reduce((acc, user) => {
				acc[user.id] = user;
				return acc;
			}, {} as Record<string, {
				id: string;
				name: string;
				profile_img: string;
				avatar_type: string;
				custom_initials: string;
			}>);
		}

		return this.usersRepositoryService.usersMap();
	});

	public readonly shownInitials = computed(() => {
		if (this.customInitials()) return this.customInitials();

		const userId = this.userId();
		if (!userId) return null;

		const user = this.avatarsMap()[userId];
		if (!user) return null;

		return user?.custom_initials ?? null;
	});

	public readonly shownLabel = computed(() => {
		if (this.label()) return this.label();

		const userId = this.userId();
		if (!userId) return null;

		const user = this.avatarsMap()[userId];
		if (!user) return null;

		return user?.name ?? null;
	});

	public readonly shownUrl = computed(() => {
		if (this.url()) return this.url();

		const userId = this.userId();
		if (!userId) return;

		const user = this.avatarsMap()[userId];
		if (!user) return;

		return user?.profile_img ?? null;
	});

	public readonly shownType = computed(() => {
		if (this.type()) return this.type();

		const userId = this.userId();
		if (!userId) return "text";

		const user = this.avatarsMap()[userId];
		if (!user) return "text";

		return user?.avatar_type || "text";
	});

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
